import React, { Fragment, useEffect, useMemo, useRef } from 'react';

import { FlexColumn } from 'src/components/flex';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import Message from 'src/pages/landlord/leads/messages/components/messages-container/Message';
import ChannelSwitchDivider from 'src/pages/landlord/leads/messages/components/messages-container/ChannelSwitchDivider';
import LeadHistoryDivider from 'src/pages/landlord/leads/messages/components/messages-container/LeadHistoryDivider';
import { computeChannelSwitches } from 'src/pages/landlord/leads/messages/components/messages-container/utils/compute-channel-switches';
import { buildThreadRows } from 'src/pages/landlord/leads/messages/components/messages-container/thread-events';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { formatTimelineDate } from 'src/utils/format-date-time';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';

export default function LeadMessages() {
    const {
        messages,
        lead,
        stages,
        interests,
        showings,
        teamMembers,
    } = useLeadMessagesPageContext();
    const { userSettings } = useAuthenticatedState();
    const isTimelineInConversation = useIsFeatureEnabled(EFeatureFlag.TIMELINE_IN_CONVERSATION);

    const messageContainerRef = useRef<HTMLDivElement>(null);
    const isPinnedToBottomRef = useRef(true);

    const channelSwitches = computeChannelSwitches(messages);
    const leadOwnerName = teamMembers.find((member) => member.id === lead.owner)?.name ?? 'Unknown';

    const showEvents = isTimelineInConversation;

    const rows = useMemo(() => buildThreadRows({
        lead,
        messages,
        stages: showEvents ? stages : [],
        interests: showEvents ? interests : [],
        tours: showEvents ? showings : [],
        channelSwitches,
        leadOwnerName,
    }), [lead, messages, stages, interests, showings, channelSwitches, leadOwnerName, showEvents]);

    // Scroll to the bottom of the message container when a new message is added
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const messageContainer = messageContainerRef.current;
            if (messageContainer) {
                messageContainer.scrollTop = messageContainer.scrollHeight;
                isPinnedToBottomRef.current = true;
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [messages.length]);

    // Notices and nudges can load after the thread and reduce its height. Keep the newest
    // row visible through those layout changes, unless the user intentionally scrolled up.
    useEffect(() => {
        const messageContainer = messageContainerRef.current;
        if (!messageContainer || !('ResizeObserver' in window)) {
            return;
        }

        const onScroll = () => {
            const distanceFromBottom = messageContainer.scrollHeight -
                messageContainer.clientHeight -
                messageContainer.scrollTop;
            isPinnedToBottomRef.current = distanceFromBottom <= 2;
        };
        const resizeObserver = new ResizeObserver(() => {
            if (isPinnedToBottomRef.current) {
                messageContainer.scrollTop = messageContainer.scrollHeight;
            }
        });

        messageContainer.addEventListener('scroll', onScroll, { passive: true });
        resizeObserver.observe(messageContainer);

        return () => {
            messageContainer.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <FlexColumn
            ref={messageContainerRef}
            className="prettyScrollbar"
            flex={1}
            p={1}
            rowGap={0}
            maxWidth="100vw"
            sx={{ overflowY: 'scroll' }}
        >
            {rows.map((row) => (
                <Fragment key={row.key}>
                    {row.type === 'event' ? (
                        <LeadHistoryDivider
                            event={row.event}
                            time={formatTimelineDate(new Date(row.at).toISOString(), userSettings.timezone_override)}
                        />
                    ) : (
                        <>
                            {row.switchedToChannel && <ChannelSwitchDivider channel={row.switchedToChannel} />}
                            <Message message={row.message} />
                        </>
                    )}
                </Fragment>
            ))}

        </FlexColumn>
    );
}
